import { z } from 'zod';
import { EXPENSE_REPORT_STATUSES, type EXPENSE_REPORT_DUTIES } from '../enums.js';
import {
  optionalDate,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { currencySchema } from './organization.js';
import type { RecordRef } from './records.js';

/**
 * Expense reports: a staff member's claim for spend, and how it is repaid.
 *
 * The totals are computed by the server from the lines. The claimant never
 * supplies one, and nobody can approve a figure that is not the sum of what
 * they were shown.
 */

const money = z.coerce.number().min(0).max(1e15);

export const expenseReportLineSchema = z.object({
  expenseDate: z.iso.date(),
  category: optionalText(80),
  description: z.string().trim().min(1, 'Required').max(500),
  amount: money.refine((value) => value > 0, 'Enter an amount greater than zero'),
});
export type ExpenseReportLineInput = z.infer<typeof expenseReportLineSchema>;

export const createExpenseReportSchema = z.object({
  title: z.string().trim().min(1, 'Required').max(300),
  currency: currencySchema.optional(),
  /** Optional: the customer invoice this spend was for. A relationship only. */
  invoiceId: uuidSchema.nullish(),
  notes: optionalText(4000),
  lines: z.array(expenseReportLineSchema).max(200).optional(),
});
export type CreateExpenseReportInput = z.infer<typeof createExpenseReportSchema>;

export const updateExpenseReportSchema = toPatchSchema(createExpenseReportSchema);
export type UpdateExpenseReportInput = z.infer<typeof updateExpenseReportSchema>;

const reason = z.string().trim().min(1, 'Say why').max(500);

export const approveExpenseReportSchema = z.object({ note: optionalText(500) });
export const rejectExpenseReportSchema = z.object({ reason });
export const cancelExpenseReportSchema = z.object({ reason });
export const returnExpenseReportSchema = z.object({ reason });
export type RejectExpenseReportInput = z.infer<typeof rejectExpenseReportSchema>;
export type CancelExpenseReportInput = z.infer<typeof cancelExpenseReportSchema>;
export type ReturnExpenseReportInput = z.infer<typeof returnExpenseReportSchema>;
export type ApproveExpenseReportInput = z.infer<typeof approveExpenseReportSchema>;

/** Handing over the cash. The amount defaults to the report's total. */
export const disburseExpenseReportSchema = z.object({
  amount: money.refine((value) => value > 0, 'Enter an amount greater than zero').optional(),
  disbursedOn: optionalDate(),
  /** The cash or bank account it came out of; when given it is posted to that ledger. */
  bankAccountId: uuidSchema.nullish(),
  notes: optionalText(500),
});
export type DisburseExpenseReportInput = z.infer<typeof disburseExpenseReportSchema>;

export const verifyExpenseReportSchema = z.object({ notes: optionalText(500) });
export type VerifyExpenseReportInput = z.infer<typeof verifyExpenseReportSchema>;

export const EXPENSE_REPORT_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'total',
  'status',
  'submittedAt',
] as const;

export const listExpenseReportsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(EXPENSE_REPORT_SORT_FIELDS).default('createdAt'),
  status: z.enum(EXPENSE_REPORT_STATUSES).optional(),
  claimantId: z.union([uuidSchema, z.literal('me')]).optional(),
  invoiceId: uuidSchema.optional(),
  /** What is waiting on the caller: reports they can approve, hand cash for, or verify. */
  queue: z.enum(['approval', 'cash', 'verification']).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type ListExpenseReportsQuery = z.infer<typeof listExpenseReportsQuerySchema>;

export interface ExpenseReportLineView {
  id: string;
  expenseDate: string;
  category: string | null;
  description: string;
  amount: string;
  position: number;
}

export interface ExpenseReportEventView {
  id: string;
  action: string;
  fromStatus: (typeof EXPENSE_REPORT_STATUSES)[number] | null;
  toStatus: (typeof EXPENSE_REPORT_STATUSES)[number] | null;
  actor: RecordRef | null;
  note: string | null;
  at: string;
}

export interface ExpenseReportSummary {
  id: string;
  number: string;
  title: string;
  status: (typeof EXPENSE_REPORT_STATUSES)[number];
  claimant: RecordRef;
  invoice: RecordRef | null;
  currency: string;
  total: string;
  disbursedAmount: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What the signed-in user may do with this report right now. */
export interface ExpenseReportAbilities {
  edit: boolean;
  submit: boolean;
  approve: boolean;
  reject: boolean;
  disburse: boolean;
  acknowledge: boolean;
  verify: boolean;
  returnToCash: boolean;
  cancel: boolean;
  reopen: boolean;
}

export interface ExpenseReportDetail extends ExpenseReportSummary {
  notes: string | null;
  lines: ExpenseReportLineView[];
  events: ExpenseReportEventView[];
  approvedBy: RecordRef | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  disbursedBy: RecordRef | null;
  disbursedAt: string | null;
  disbursementNotes: string | null;
  disbursementAccount: RecordRef | null;
  acknowledgedAt: string | null;
  verifiedBy: RecordRef | null;
  verifiedAt: string | null;
  verificationNotes: string | null;
  cancellationReason: string | null;
  abilities: ExpenseReportAbilities;
}

// ---- Books settings: who holds which duty, and the GST bank account ----

const dutyHolders = z.array(uuidSchema).max(50).optional();
export const updateExpenseReportAssignmentsSchema = z.object({
  /** For each duty, the people who hold it. An empty list means "anyone whose role allows it". */
  ACCOUNTANT: dutyHolders,
  APPROVER: dutyHolders,
  CASH_DISBURSER: dutyHolders,
});
export type UpdateExpenseReportAssignmentsInput = z.infer<
  typeof updateExpenseReportAssignmentsSchema
>;

export const updateBooksSettingsSchema = z.object({
  enforceSeparationOfDuties: z.boolean().optional(),
  gstBankAccountId: uuidSchema.nullish(),
  gstPaymentInstructions: optionalText(1000),
});
export type UpdateBooksSettingsInput = z.infer<typeof updateBooksSettingsSchema>;

export interface BooksSettingsView {
  enforceSeparationOfDuties: boolean;
  gstBankAccount: (RecordRef & { bankName: string; currency: string }) | null;
  gstPaymentInstructions: string | null;
  assignments: Record<(typeof EXPENSE_REPORT_DUTIES)[number], RecordRef[]>;
  /** Active people who can be given a duty, for the picker. */
  people: RecordRef[];
}

// ---- Books dashboard ----

export interface BooksDashboardCard {
  key: string;
  title: string;
  /** Records behind the card. The link opens the list filtered to exactly these. */
  count: number;
  /** Money behind the card, one entry per currency — currencies are never added together. */
  amounts: { currency: string; amount: string }[];
  /** Where the card goes: the matching list, already filtered. */
  href: string;
  group: 'sales' | 'purchasing' | 'expenses' | 'gst';
}

export interface BooksDashboard {
  generatedAt: string;
  cards: BooksDashboardCard[];
}
